import { useEffect, useMemo, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'

import { db } from '@/lib/firebase'
import { useArtistsData } from '@/contexts/ArtistsDataContext'
import type { CommunityPost } from '@/lib/communityPostData'
import { shouldShowVerifiedArtistCheckmark } from '@/lib/communityAuthorDisplay'

interface CommunityPostAuthorProfile {
  readonly avatarUrl: string | null
  readonly authorDisplayName: string
  readonly profileHref: string | null
  readonly showVerifiedArtistCheckmark: boolean
  readonly avatarInitial: string
  readonly loading: boolean
}

export function useCommunityPostAuthorProfile(
  post: CommunityPost | null,
  anonymousAuthorLabel: string,
): CommunityPostAuthorProfile {
  const { allArtists, availableArtistIds } = useArtistsData()
  const [userAvatarUrl, setUserAvatarUrl] = useState<string | null>(null)
  const [userProfileLoading, setUserProfileLoading] = useState(false)

  const matchedArtist = useMemo(() => {
    const artistId = post?.artistId?.trim()
    if (!artistId) {
      return null
    }
    return allArtists.find((artist) => artist.id === artistId) ?? null
  }, [allArtists, post?.artistId])

  const authorDisplayName =
    post?.authorDisplayName?.trim() || matchedArtist?.displayName?.trim() || anonymousAuthorLabel

  const avatarInitial = (authorDisplayName.replace(/^@/u, '')[0] ?? '?').toUpperCase()

  const showVerifiedArtistCheckmark = post
    ? shouldShowVerifiedArtistCheckmark({
        authorRole: post.authorRole,
        artistId: post.artistId,
        liveArtistIds: availableArtistIds,
      })
    : false

  const profileHref =
    post?.artistId && availableArtistIds.has(post.artistId) ? `/artist/${post.artistId}` : null

  const artistAvatarUrl =
    matchedArtist?.compressedAvatarUrl?.trim() || matchedArtist?.avatarUrl?.trim() || null

  useEffect(() => {
    if (!post?.userId || artistAvatarUrl) {
      setUserAvatarUrl(null)
      setUserProfileLoading(false)
      return
    }

    let cancelled = false
    setUserProfileLoading(true)

    void getDoc(doc(db, 'users', post.userId))
      .then((userSnapshot) => {
        if (cancelled) {
          return
        }
        const userData = userSnapshot.data()
        const resolvedAvatarUrl =
          typeof userData?.avatarUrl === 'string' && userData.avatarUrl.trim().length > 0
            ? userData.avatarUrl.trim()
            : null
        setUserAvatarUrl(resolvedAvatarUrl)
      })
      .catch(() => {
        if (!cancelled) {
          setUserAvatarUrl(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setUserProfileLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [artistAvatarUrl, post?.userId])

  return {
    avatarUrl: artistAvatarUrl ?? userAvatarUrl,
    authorDisplayName,
    profileHref,
    showVerifiedArtistCheckmark,
    avatarInitial,
    loading: userProfileLoading && !artistAvatarUrl,
  }
}
